/**
 * Body of POST PendingTasks. The saga adds `companyId` (selected company) from the store, plus
 * `lang` (current language) for the search endpoint only. The backend takes the assignee from the
 * token, so it is not sent.
 *
 * Filters without a value are left out (undefined, so JSON.stringify drops them): the body only
 * shows the filters that are applied.
 *
 * @typedef {Object} PendingTasksRequest
 * @property {number} pageNumber       1-based page
 * @property {number} recordsByPage    page size
 * @property {string} [searchParam]    free-text search (omitted = none)
 * @property {string} [startDateTime]  dd/MM/yyyy (omitted = none)
 * @property {string} [endDateTime]    dd/MM/yyyy (omitted = none)
 * @property {string} sortField        column to sort by
 * @property {"asc"|"desc"} sortDirection
 */

/**
 * Builds a PendingTasksRequest with defaults filled in.
 * @returns {PendingTasksRequest}
 */
export const createPendingTasksRequest = (params = {}) => ({
	pageNumber: params.pageNumber || 1,
	recordsByPage: params.recordsByPage || 10,
	searchParam: params.searchParam?.trim() || undefined,
	startDateTime: params.startDate || undefined,
	endDateTime: params.endDate || undefined,
	sortField: params.sortField || "modified_at",
	sortDirection: params.sortDirection || "desc",
});

/**
 * One task from POST PendingTasks (a tbl_workflow_tasks row). Field names stay snake_case
 * because the PendingTasks column config and button rules (tbl_table_column_configs) use them.
 * process_definition_key and task_name are keys; the table translates them (translateValue).
 *
 * @typedef {Object} PendingTaskResponse
 * @property {string} task_id
 * @property {string} process_instance_id
 * @property {string} process_definition_key
 * @property {string} task_definition_key
 * @property {string} task_name
 * @property {string|null} form_key
 * @property {string} assignee
 * @property {string} started_at
 * @property {string} modified_at
 * @property {boolean} can_delete   Delete would be allowed now (permission + the workflow's delete
 *                                  policy). Only shows/hides the button; the backend checks again.
 */

/**
 * Maps one task from api/WorkflowTasks/PendingTasks or .../PendingTasks/Search (camelCase, same
 * shape from both) to a PendingTaskResponse.
 * @returns {PendingTaskResponse}
 */
export const mapPendingTaskResponse = (task = {}) => ({
	task_id: task.taskId,
	process_instance_id: task.processInstanceId,
	process_definition_key: task.processDefinitionKey,
	task_definition_key: task.taskDefinitionKey,
	task_name: task.taskName,
	form_key: task.formKey ?? null,
	assignee: task.assignee,
	started_at: task.startedAt,
	modified_at: task.modifiedAt,
	can_delete: task.canDelete === true,
});

/**
 * One generated document of a process (POST api/WorkflowTasks/Documents), e.g. one meeting report.
 * Only "ready" documents can be downloaded; "pending" is still being generated, "failed" can be
 * retried by an admin.
 *
 * @typedef {Object} ProcessDocumentResponse
 * @property {string} documentId
 * @property {string} displayName   name to show (falls back to "Document")
 * @property {"pending"|"ready"|"failed"} status
 * @property {string|null} taskId   the task run it was made for (e.g. which meeting)
 * @property {string|null} createdAt
 */

/**
 * Maps one document from POST api/WorkflowTasks/Documents to a ProcessDocumentResponse.
 * @returns {ProcessDocumentResponse}
 */
export const mapProcessDocumentResponse = (document = {}) => ({
	documentId: document.documentId,
	displayName: document.displayName || "Document",
	status: document.status,
	taskId: document.taskId ?? null,
	createdAt: document.createdAt ?? null,
});
